export type DocumentOrderNavigation<SectionId> = {
  readonly previous: SectionId | null;
  readonly next: SectionId | null;
};

export const documentOrderNavigation = <SectionId>({
  orderedIds,
  currentId,
}: {
  readonly orderedIds: readonly SectionId[];
  readonly currentId: SectionId | null;
}): DocumentOrderNavigation<SectionId> => {
  const index = currentId === null ? -1 : orderedIds.indexOf(currentId);
  return {
    previous: index > 0 ? orderedIds[index - 1] ?? null : null,
    next: index >= 0 && index < orderedIds.length - 1 ? orderedIds[index + 1] ?? null : null,
  };
};

export const mappingSectionSelection = <SectionId>({
  orderedIds,
  requestedId,
  currentId,
  preferredId,
}: {
  readonly orderedIds: readonly SectionId[];
  readonly requestedId: SectionId | null;
  readonly currentId: SectionId | null;
  readonly preferredId?: SectionId | null;
}): SectionId | null => {
  const valid = (value: SectionId | null | undefined): value is SectionId =>
    value !== null && value !== undefined && orderedIds.includes(value);
  if (valid(requestedId)) return requestedId;
  if (valid(currentId)) return currentId;
  if (valid(preferredId)) return preferredId;
  return orderedIds[0] ?? null;
};

export const exactMappingDecisionAvailable = <SectionId>({
  selectedId,
  mappedSectionIds,
  leafSelected,
  evidenceAvailable,
}: {
  readonly selectedId: SectionId | null;
  readonly mappedSectionIds: readonly SectionId[];
  readonly leafSelected: boolean;
  readonly evidenceAvailable: boolean;
}): boolean => selectedId !== null && leafSelected && evidenceAvailable && mappedSectionIds.includes(selectedId);

export type MappingPanelState = "noHierarchy" | "noSelection" | "empty" | "ready";

export const mappingPanelState = ({ sectionCount, hasSelection, mappingCount }: {
  readonly sectionCount: number;
  readonly hasSelection: boolean;
  readonly mappingCount: number;
}): MappingPanelState => {
  if (sectionCount === 0) return "noHierarchy";
  if (!hasSelection) return "noSelection";
  return mappingCount === 0 ? "empty" : "ready";
};
